import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ArrowLeft, Bookmark, Check, Download, Edit3, FileUp, GripVertical, MapPin, Plus, Trash2, Upload, Users } from "lucide-react";
import { formatCrewPhone } from "../utils/phone";
import PortalDropdown from "../components/PortalDropdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

const EMPTY_CREW = {
  company_crew_id: "",
  name: "",
  role: "",
  department: "",
  phone: "",
  email: "",
  notes: "",
  category: "crew",
};

const EMPTY_LOCATION = {
  company_location_id: "",
  name: "",
  location_name: "",
  location_address: "",
  parking_address: "",
  hospital_name: "",
  hospital_address: "",
  hospital_phone: "",
  hospital_er_notes: "",
  notes: "",
};

const inputClass = "w-full rounded border border-[#28303F] bg-[#0D0F12] px-3 py-2 text-sm ";
const inputFocus = "text-white focus:border-amber-500 focus:outline-none";

/**
 * Autocomplete name field: type to search `source` by name, pick a suggestion
 * to autofill via `onPick(entry)`, or just finish typing to keep a brand-new
 * entry. Mirrors the Talent autocomplete in the call-sheet builder.
 */
function AutocompleteNameInput({
  value,
  onChange,
  onPick,
  source,
  placeholder,
  testid,
  required = false,
  emptyHint = "",
}) {
  const wrapRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return (source || []).slice(0, 8);
    return (source || [])
      .filter((entry) => (entry.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [source, q]);
  const showList = focused && matches.length > 0;

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={value}
        required={required}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        placeholder={placeholder || (source?.length ? "Type to search or add new…" : emptyHint)}
        autoComplete="off"
        data-testid={testid}
        className={`${inputClass}${inputFocus} mt-1`}
      />
      <PortalDropdown
        anchorRef={wrapRef}
        open={showList}
        maxHeight={240}
        className="bg-[#141922] border border-amber-500/30 rounded-lg shadow-xl overflow-hidden"
      >
        <div data-testid={testid ? `${testid}-suggestions` : undefined}>
          {matches.map((entry, idx) => (
            <button
              key={entry.id || idx}
              type="button"
              data-testid={testid ? `${testid}-suggestion-${idx}` : undefined}
              onMouseDown={(event) => { event.preventDefault(); onPick(entry); setFocused(false); }}
              className="w-full flex items-start justify-between gap-2 text-left px-3 py-1.5 text-xs text-gray-200 hover:bg-amber-500/10 hover:text-white"
            >
              <span className="min-w-0 flex-1 font-medium truncate">{entry.name}</span>
              <span className="shrink-0 text-right text-[10px] text-gray-500 truncate max-w-[55%]">
                {entry.role || entry.location_address || entry.email || entry.phone || "—"}
              </span>
            </button>
          ))}
        </div>
      </PortalDropdown>
    </div>
  );
}


function DepartmentChipPicker({ departments, value, onChange, onAddDepartment, templateSuggestions = [] }) {
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputWrapRef = useRef(null);

  // Merge saved project departments + unused template names (dedupe, CI).
  const combined = Array.from(
    new Map(
      [
        ...(departments || []).map((d) => ({ name: d, kind: "project" })),
        ...(templateSuggestions || []).map((d) => ({ name: d, kind: "template" })),
      ]
        .filter((e) => e.name)
        .map((e) => [e.name.toLowerCase(), e]),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const q = query.trim().toLowerCase();
  const matches = q
    ? combined.filter((e) => e.name.toLowerCase().includes(q)).slice(0, 10)
    : combined.slice(0, 10);
  const exactMatch = combined.find((e) => e.name.toLowerCase() === q);
  const canCreate = q.length > 0 && !exactMatch;
  const showList = focused && (matches.length > 0 || canCreate);

  const pick = async (entry) => {
    if (entry.kind === "project") {
      onChange(entry.name);
      setQuery("");
      setFocused(false);
      return;
    }
    if (adding) return;
    setAdding(true);
    try {
      await onAddDepartment(entry.name);
      onChange(entry.name);
      setQuery("");
      setFocused(false);
    } finally {
      setAdding(false);
    }
  };

  const createFromQuery = async () => {
    const name = query.trim();
    if (!name || adding) return;
    const inProject = (departments || []).find((d) => (d || "").toLowerCase() === name.toLowerCase());
    if (inProject) {
      onChange(inProject);
      setQuery("");
      setFocused(false);
      return;
    }
    setAdding(true);
    try {
      await onAddDepartment(name);
      onChange(name);
      setQuery("");
      setFocused(false);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="project-crew-department-picker">
      <p className="text-xs text-gray-500">Department</p>
      {value && (
        <div className="mt-1 flex items-center gap-2" data-testid="project-crew-department-selected">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500 bg-amber-500/15 px-2.5 py-1 text-xs text-amber-300">
            <Check size={11} /> {value}
          </span>
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs text-gray-600 hover:text-red-400"
            data-testid="project-crew-department-clear"
          >
            Clear
          </button>
        </div>
      )}
      <div ref={inputWrapRef} className="relative mt-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              createFromQuery();
            }
          }}
          autoComplete="off"
          placeholder={value ? "Change department — type to search or add…" : "Type to search or add a department…"}
          data-testid="project-crew-new-department-input"
          className="w-full rounded border border-[#28303F] bg-[#0D0F12] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
        />
        <PortalDropdown
          anchorRef={inputWrapRef}
          open={showList}
          maxHeight={260}
          className="bg-[#141922] border border-amber-500/30 rounded-lg shadow-xl overflow-hidden"
        >
          <div data-testid="project-crew-new-department-suggestions">
            {matches.map((entry, idx) => (
              <button
                key={entry.name}
                type="button"
                data-testid={`project-crew-new-department-suggestion-${idx}`}
                onMouseDown={(event) => { event.preventDefault(); pick(entry); }}
                className="w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs text-gray-200 hover:bg-amber-500/10 hover:text-white"
              >
                <span className="flex items-center gap-2 min-w-0">
                  {entry.kind === "project"
                    ? <Check size={11} className="text-emerald-400/80 shrink-0" />
                    : <Plus size={11} className="text-amber-400/70 shrink-0" />}
                  <span className="truncate">{entry.name}</span>
                </span>
                <span className="text-[10px] text-gray-500 shrink-0">
                  {entry.kind === "project" ? "on project" : "add from templates"}
                </span>
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                data-testid="project-crew-new-department-create"
                onMouseDown={(event) => { event.preventDefault(); createFromQuery(); }}
                className="w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs text-amber-300 hover:bg-amber-500/15 border-t border-[#28303F]"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Plus size={11} className="shrink-0" />
                  <span className="truncate">Create "{query.trim()}"</span>
                </span>
                <span className="text-[10px] text-gray-500 shrink-0">new department</span>
              </button>
            )}
          </div>
        </PortalDropdown>
      </div>
    </div>
  );
}

function DepartmentManager({ project, projectId, deptTemplates, setDeptTemplates, onUpdate }) {
  const departments = project?.departments || [];
  const [addInput, setAddInput] = useState("");
  const [showAddInput, setShowAddInput] = useState(false);

  // Flatten all department names from every saved template, dedupe (CI), and
  // drop any already present on this project — these drive the autocomplete.
  const existingLower = new Set(departments.map((d) => (d || "").toLowerCase()));
  const templateSuggestions = Array.from(
    new Map(
      (deptTemplates || [])
        .flatMap((t) => t.departments || [])
        .filter((d) => d && !existingLower.has(d.toLowerCase()))
        .map((d) => [d.toLowerCase(), d]),
    ).values(),
  ).sort();

  const saveDepartments = async (nextList) => {
    onUpdate(nextList);
    try {
      await axios.put(
        `${API}/projects/${projectId}`,
        { ...project, departments: nextList, description: project.description || "" },
        ax(),
      );
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save departments");
    }
  };

  return (
    <section
      data-testid="project-departments-manager"
      className="rounded-xl border border-[#28303F] bg-[#1A1F29] p-4"
    >
      <div className="mb-3 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <div className="min-w-32 text-xs uppercase tracking-wider text-gray-500">Departments</div>
        <p className="min-w-0 flex-1 text-xs text-gray-500">
          Feeds the Department picker when adding crew — and drives the printed sort order on every PDF.
        </p>

        {deptTemplates.length > 0 && (
          <select
            data-testid="project-dept-template-apply"
            defaultValue=""
            onChange={async (e) => {
              const tid = e.target.value;
              e.target.value = "";
              if (!tid) return;
              const tpl = deptTemplates.find((x) => x.id === tid);
              if (!tpl) return;
              const merge = window.confirm(
                `Apply "${tpl.name}"?\n\nOK = MERGE with existing.\nCancel = REPLACE existing.`,
              );
              const list = merge
                ? [...new Set([...(departments), ...(tpl.departments || [])])]
                : [...(tpl.departments || [])];
              saveDepartments(list);
            }}
            className="rounded border border-[#28303F] bg-[#0D0F12] px-2.5 py-1.5 text-xs text-gray-300 focus:border-amber-500 focus:outline-none"
          >
            <option value="">Apply Template…</option>
            {deptTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} ({(tpl.departments || []).length})
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          data-testid="project-dept-save-template-btn"
          disabled={departments.length === 0}
          onClick={async () => {
            const name = window.prompt("Template name")?.trim();
            if (!name) return;
            try {
              const { data } = await axios.post(
                `${API}/department-templates`,
                { name, departments: departments },
                ax(),
              );
              setDeptTemplates((prev) =>
                [...prev, data].sort((a, b) => a.name.localeCompare(b.name)),
              );
              alert(`Saved department template "${data.name}".`);
            } catch (err) {
              alert(err.response?.data?.detail || "Failed to save template");
            }
          }}
          className="flex items-center gap-1.5 rounded-lg border border-[#28303F] px-3 py-1.5 text-xs text-gray-300 hover:border-amber-500/40 hover:text-amber-400 disabled:opacity-40"
        >
          <Bookmark size={12} /> Save as Template
        </button>

        <button
          type="button"
          data-testid="project-dept-add-btn"
          onClick={() => {
            setShowAddInput((prev) => !prev);
            setAddInput("");
          }}
          className="flex items-center gap-1.5 rounded-lg border border-amber-500/40 px-3 py-1.5 text-xs text-amber-400 hover:bg-amber-500/10"
        >
          <Plus size={12} /> Add Department
        </button>
      </div>

      {showAddInput && (
        <div className="mb-3 flex gap-2" data-testid="project-dept-add-input-row">
          <input
            autoFocus
            value={addInput}
            list="dept-template-suggestions"
            autoComplete="off"
            onChange={(e) => setAddInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const name = addInput.trim();
                if (!name) return;
                if (departments.some((d) => d.toLowerCase() === name.toLowerCase())) {
                  alert("Already added.");
                  return;
                }
                saveDepartments([...departments, name]);
                setAddInput("");
                setShowAddInput(false);
              } else if (e.key === "Escape") {
                e.preventDefault();
                setShowAddInput(false);
                setAddInput("");
              }
            }}
            placeholder={
              templateSuggestions.length
                ? "Type a new department or pick one from your templates…"
                : "Type a new department and press Enter"
            }
            data-testid="project-dept-add-input"
            className="flex-1 rounded border border-[#28303F] bg-[#0D0F12] px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
          />
          <datalist id="dept-template-suggestions">
            {templateSuggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <button
            type="button"
            onClick={() => {
              const name = addInput.trim();
              if (!name) return;
              if (departments.some((d) => d.toLowerCase() === name.toLowerCase())) {
                alert("Already added.");
                return;
              }
              saveDepartments([...departments, name]);
              setAddInput("");
              setShowAddInput(false);
            }}
            disabled={!addInput.trim()}
            data-testid="project-dept-add-save-btn"
            className="rounded bg-amber-500 px-3 py-2 text-xs font-bold text-black transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="project-dept-chip-list">
        {departments.length === 0 && (
          <p className="text-xs text-gray-600">No departments yet — click "Add Department".</p>
        )}
        {departments.length > 1 && (
          <p className="-mb-1 w-full text-[10px] text-gray-600">
            Drag any chip to reorder — this drives the order on every PDF.
          </p>
        )}
        {departments.map((d, idx) => (
          <span
            key={d + idx}
            data-testid="project-dept-chip"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", String(idx));
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              e.currentTarget.classList.add("!border-amber-500/60");
            }}
            onDragLeave={(e) => e.currentTarget.classList.remove("!border-amber-500/60")}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("!border-amber-500/60");
              const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
              const to = idx;
              if (isNaN(from) || from === to) return;
              const list = [...departments];
              const [item] = list.splice(from, 1);
              list.splice(to, 0, item);
              saveDepartments(list);
            }}
            className="group flex cursor-grab items-center gap-1.5 rounded-full border border-[#28303F] bg-[#0D0F12] py-1 pl-3 pr-1 text-xs text-gray-300 transition-colors active:cursor-grabbing"
          >
            <GripVertical size={11} className="-ml-1 text-gray-600" />
            {d}
            <button
              type="button"
              title="Remove"
              onClick={() => {
                if (!window.confirm(`Remove "${d}" from departments?`)) return;
                saveDepartments(departments.filter((x) => x !== d));
              }}
              className="flex h-5 w-5 items-center justify-center rounded-full leading-none text-gray-600 hover:text-red-400"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </section>
  );
}

function Modal({ title, children, onClose, size = "xl" }) {
  const widthClass = size === "4xl" ? "max-w-4xl" : size === "2xl" ? "max-w-2xl" : "max-w-xl";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className={`max-h-[90vh] w-full ${widthClass} overflow-y-auto rounded-xl border border-[#28303F] bg-[#14181F] shadow-2xl`}>
        <div className="sticky top-0 flex items-center justify-between border-b border-[#28303F] bg-[#14181F] p-5">
          <h2 className="font-[Outfit] text-lg font-bold text-white">{title}</h2>
          <button
            type="button"
            data-testid="project-database-modal-close"
            onClick={onClose}
            className="text-2xl leading-none text-gray-500 hover:text-white"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CrewCsvImportModal({ projectId, project, onClose, onComplete }) {
  const fileRef = useRef(null);
  const [stage, setStage] = useState("pick"); // pick → preview → done
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // {rows, departments_in_csv, project_departments, stats, warnings}
  const [editedRows, setEditedRows] = useState([]);
  const [deptMap, setDeptMap] = useState({}); // csvDept(lower) → chosen project dept or "__new__"
  const [newDeptNames, setNewDeptNames] = useState({}); // csvDept(lower) → new dept name string
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const projectDeptLower = useMemo(
    () => (project?.departments || []).map((d) => (d || "").toLowerCase()),
    [project?.departments],
  );

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/projects/${projectId}/crew/import-csv/preview`,
        fd,
        { ...ax(), headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      setEditedRows(data.rows);
      // Auto-map: CSV dept name → matching project dept (case-insensitive) OR "__new__"
      const initialMap = {};
      const initialNew = {};
      (data.departments_in_csv || []).forEach((d) => {
        const match = (data.project_departments || []).find(
          (p) => (p || "").toLowerCase() === d.toLowerCase(),
        );
        if (match) {
          initialMap[d.toLowerCase()] = match;
        } else {
          initialMap[d.toLowerCase()] = "__new__";
          initialNew[d.toLowerCase()] = d;
        }
      });
      setDeptMap(initialMap);
      setNewDeptNames(initialNew);
      setStage("preview");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not read CSV file");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const csv = "Name,Phone,Email,Department,Position,Notes\nJane Smith,555-0101,jane@example.com,Camera,Director of Photography,\nBob Jones,555-0102,bob@example.com,Grip,Key Grip,";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "project_crew_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const updateRow = (idx, field, value) => {
    setEditedRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const commitImport = async () => {
    setCommitting(true);
    setError("");
    try {
      // Resolve the final department name for each row from the mapping
      const resolvedRows = editedRows
        .filter((r) => r.name && !r.issues?.some((i) => i.startsWith("Already")))
        .map((r) => {
          const key = (r.department || "").toLowerCase();
          let finalDept = r.department;
          if (deptMap[key] === "__new__") {
            finalDept = (newDeptNames[key] || r.department || "").trim();
          } else if (deptMap[key]) {
            finalDept = deptMap[key];
          }
          return {
            name: r.name,
            phone: r.phone,
            email: r.email,
            department: finalDept,
            role: r.role,
            notes: r.notes,
          };
        });

      // Collect new departments to create
      const newDepartments = Object.entries(deptMap)
        .filter(([, choice]) => choice === "__new__")
        .map(([key]) => (newDeptNames[key] || key).trim())
        .filter((n) => n && !projectDeptLower.includes(n.toLowerCase()));

      const { data } = await axios.post(
        `${API}/projects/${projectId}/crew/import-csv/commit`,
        { rows: resolvedRows, new_departments: newDepartments },
        ax(),
      );
      setResult(data);
      setStage("done");
      onComplete(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not import CSV");
    } finally {
      setCommitting(false);
    }
  };

  const importableCount = editedRows.filter(
    (r) => r.name && !r.issues?.some((i) => i.startsWith("Already") || i.startsWith("Missing")),
  ).length;

  return (
    <Modal title="Import Crew from CSV" onClose={onClose} size="4xl">
      <div className="space-y-5 p-5">
        {stage === "pick" && (
          <>
            <p className="text-sm text-gray-400">
              Upload a CSV to bulk-add crew to this project. We'll show you a preview before saving anything.
              Columns recognized: <span className="text-amber-300">Name, Phone, Email, Department, Position/Role, Notes</span>.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="crew-csv-pick-file-button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 rounded bg-amber-500 px-4 py-2.5 text-sm font-bold text-black hover:bg-amber-600 disabled:opacity-50"
              >
                <Upload size={15} /> {uploading ? "Reading…" : "Choose CSV file"}
              </button>
              <button
                type="button"
                onClick={downloadTemplate}
                data-testid="crew-csv-template-button"
                className="flex items-center gap-2 rounded border border-[#28303F] px-4 py-2.5 text-sm text-gray-300 hover:border-amber-500/40 hover:text-amber-400"
              >
                <Download size={15} /> Download template
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} data-testid="crew-csv-file-input" className="hidden" />
            </div>
            {error && (
              <p className="rounded border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300" data-testid="crew-csv-error">{error}</p>
            )}
          </>
        )}

        {stage === "preview" && preview && (
          <>
            {(preview.warnings || []).map((w, i) => (
              <p key={i} className="flex items-center gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200" data-testid="crew-csv-warning">
                <AlertTriangle size={13} /> {w}
              </p>
            ))}
            <div className="rounded-lg border border-[#28303F] bg-[#0D0F12] p-3 text-xs text-gray-400">
              <span className="text-white font-bold">{preview.stats.total}</span> rows read ·
              <span className="text-emerald-400 font-bold"> {importableCount}</span> ready to import ·
              <span className="text-gray-500"> {preview.stats.total - importableCount}</span> will be skipped
            </div>

            {preview.departments_in_csv?.length > 0 && (
              <div data-testid="crew-csv-dept-mapping">
                <h3 className="mb-2 text-sm font-bold text-white">Map CSV departments to this project</h3>
                <div className="space-y-2">
                  {preview.departments_in_csv.map((dept) => {
                    const key = dept.toLowerCase();
                    const choice = deptMap[key] || "__new__";
                    return (
                      <div key={key} className="flex flex-wrap items-center gap-2 rounded border border-[#28303F] bg-[#0D0F12] p-2.5 text-xs">
                        <span className="rounded-full bg-[#1A1F29] px-2.5 py-1 font-mono text-amber-300">CSV: {dept}</span>
                        <span className="text-gray-600">→</span>
                        <select
                          value={choice}
                          onChange={(e) => setDeptMap((m) => ({ ...m, [key]: e.target.value }))}
                          data-testid="crew-csv-dept-select"
                          className="rounded border border-[#28303F] bg-[#14181F] px-2 py-1 text-xs text-white focus:border-amber-500 focus:outline-none"
                        >
                          {(preview.project_departments || []).map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                          <option value="__new__">+ Create new department</option>
                        </select>
                        {choice === "__new__" && (
                          <input
                            value={newDeptNames[key] || ""}
                            onChange={(e) => setNewDeptNames((n) => ({ ...n, [key]: e.target.value }))}
                            placeholder="New department name"
                            data-testid="crew-csv-new-dept-input"
                            className="flex-1 min-w-[160px] rounded border border-[#28303F] bg-[#14181F] px-2 py-1 text-xs text-white focus:border-amber-500 focus:outline-none"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-2 text-sm font-bold text-white">Preview</h3>
              <div className="max-h-80 overflow-auto rounded-lg border border-[#28303F]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#1A1F29] text-left text-gray-400">
                    <tr>
                      <th className="px-2 py-1.5">#</th>
                      <th className="px-2 py-1.5">Name</th>
                      <th className="px-2 py-1.5">Phone</th>
                      <th className="px-2 py-1.5">Email</th>
                      <th className="px-2 py-1.5">Department</th>
                      <th className="px-2 py-1.5">Position</th>
                      <th className="px-2 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedRows.map((row, idx) => {
                      const willSkip = !row.name || row.issues?.some((i) => i.startsWith("Already") || i.startsWith("Missing"));
                      return (
                        <tr
                          key={idx}
                          data-testid="crew-csv-preview-row"
                          className={`border-t border-[#28303F] ${willSkip ? "opacity-50" : "text-white"}`}
                        >
                          <td className="px-2 py-1 font-mono text-gray-500">{row.row_number}</td>
                          <td className="px-2 py-1"><input value={row.name} onChange={(e) => updateRow(idx, "name", e.target.value)} className="w-full bg-transparent focus:outline-none" /></td>
                          <td className="px-2 py-1"><input value={row.phone} onChange={(e) => updateRow(idx, "phone", formatCrewPhone(e.target.value))} className="w-full bg-transparent focus:outline-none" /></td>
                          <td className="px-2 py-1"><input value={row.email} onChange={(e) => updateRow(idx, "email", e.target.value)} className="w-full bg-transparent focus:outline-none" /></td>
                          <td className="px-2 py-1 text-amber-300/80">{row.department || "—"}</td>
                          <td className="px-2 py-1"><input value={row.role} onChange={(e) => updateRow(idx, "role", e.target.value)} className="w-full bg-transparent focus:outline-none" /></td>
                          <td className="px-2 py-1">
                            {row.issues?.length ? (
                              <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-red-300" data-testid="crew-csv-row-issue">{row.issues[0]}</span>
                            ) : row.exists_in_company ? (
                              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">Will add to project</span>
                            ) : (
                              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-300">New</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {error && (
              <p className="rounded border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300">{error}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setStage("pick"); setPreview(null); setEditedRows([]); }}
                className="flex-1 rounded border border-[#28303F] py-2.5 text-sm text-gray-300 hover:text-white"
              >
                Back
              </button>
              <button
                type="button"
                onClick={commitImport}
                disabled={committing || importableCount === 0}
                data-testid="crew-csv-commit-button"
                className="flex-1 rounded bg-amber-500 py-2.5 text-sm font-bold text-black hover:bg-amber-600 disabled:opacity-50"
              >
                {committing ? "Importing…" : `Import ${importableCount} crew member${importableCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}

        {stage === "done" && result && (
          <div className="space-y-4 text-center" data-testid="crew-csv-done">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
              <Check size={30} />
            </div>
            <h3 className="font-[Outfit] text-xl font-bold text-white">Import complete</h3>
            <p className="text-sm text-gray-400">
              <span className="text-emerald-400 font-bold">{result.imported}</span> added ·
              <span className="text-amber-300 font-bold"> {result.updated}</span> updated ·
              <span className="text-gray-500"> {result.skipped}</span> skipped
            </p>
            {result.added_departments?.length > 0 && (
              <p className="text-xs text-gray-500">
                Created {result.added_departments.length} new department{result.added_departments.length === 1 ? "" : "s"}: {result.added_departments.join(", ")}
              </p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-amber-500 px-6 py-2.5 text-sm font-bold text-black hover:bg-amber-600"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function ProjectDatabases({ view = "crew" }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [crew, setCrew] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companyCrew, setCompanyCrew] = useState([]);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [crewModal, setCrewModal] = useState(null);
  const [locationModal, setLocationModal] = useState(null);
  const [crewForm, setCrewForm] = useState(EMPTY_CREW);
  const [locationForm, setLocationForm] = useState(EMPTY_LOCATION);
  const [saving, setSaving] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [deptTemplates, setDeptTemplates] = useState([]);

  const isTalent = view === "talent";
  const isLocationsView = view === "locations";
  const category = isTalent ? "talent" : "crew";

  const companyCrewById = useMemo(
    () => Object.fromEntries(companyCrew.map((member) => [member.id, member])),
    [companyCrew],
  );
  const companyLocationById = useMemo(
    () => Object.fromEntries(companyLocations.map((location) => [location.id, location])),
    [companyLocations],
  );

  const refresh = async () => {
    const requests = [axios.get(`${API}/projects/${projectId}`, ax())];
    if (isLocationsView) {
      requests.push(axios.get(`${API}/projects/${projectId}/locations`, ax()));
      requests.push(axios.get(`${API}/projects/${projectId}/company-locations`, ax()));
    } else {
      requests.push(axios.get(`${API}/projects/${projectId}/crew?category=${category}`, ax()));
      requests.push(axios.get(`${API}/projects/${projectId}/company-crew`, ax()));
    }
    const [projectRes, listRes, companyRes] = await Promise.all(requests);
    setProject(projectRes.data);
    if (isLocationsView) {
      setLocations(listRes.data);
      setCompanyLocations(companyRes.data);
    } else {
      setCrew(listRes.data);
      setCompanyCrew(companyRes.data);
    }
  };

  useEffect(() => {
    refresh().catch(() => navigate("/dashboard"));
    // Fetch department templates once per view — only needed on the Crew page
    if (!isTalent && !isLocationsView) {
      axios.get(`${API}/department-templates`, ax())
        .then(({ data }) => setDeptTemplates(data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, view]);

  const openCrew = (member = null) => {
    setCrewForm(member
      ? { ...EMPTY_CREW, ...member, phone: formatCrewPhone(member.phone || ""), category }
      : { ...EMPTY_CREW, category });
    setCrewModal(member || "add");
  };

  const chooseCompanyCrew = (companyCrewId) => {
    const member = companyCrewById[companyCrewId];
    if (!member) return;
    setCrewForm((current) => ({
      ...current,
      company_crew_id: companyCrewId,
      name: member.name || "",
      phone: formatCrewPhone(member.phone || ""),
      email: member.email || "",
      notes: member.notes || "",
    }));
  };

  const saveCrew = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const endpoint = crewModal === "add"
        ? `${API}/projects/${projectId}/crew`
        : `${API}/projects/${projectId}/crew/${crewModal.id}`;
      const method = crewModal === "add" ? "post" : "put";
      await axios[method](endpoint, crewForm, ax());
      setCrewModal(null);
      await refresh();
    } catch (error) {
      alert(error.response?.data?.detail || "Could not save project crew");
    } finally {
      setSaving(false);
    }
  };

  const addProjectDepartment = async (name) => {
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/departments`, { name }, ax());
      setProject((current) => (current ? { ...current, departments: data.departments } : current));
    } catch (error) {
      alert(error.response?.data?.detail || "Could not add department");
      throw error;
    }
  };

  const removeCrew = async (member) => {
    if (!window.confirm(`Remove ${member.name} from this project?`)) return;
    await axios.delete(`${API}/projects/${projectId}/crew/${member.id}`, ax());
    await refresh();
  };

  const openLocation = (location = null) => {
    setLocationForm(location ? { ...EMPTY_LOCATION, ...location } : EMPTY_LOCATION);
    setLocationModal(location || "add");
  };

  const chooseCompanyLocation = (companyLocationId) => {
    const location = companyLocationById[companyLocationId];
    if (!location) return;
    setLocationForm((current) => ({
      ...current,
      company_location_id: companyLocationId,
      name: location.name || "",
      location_name: location.location_name || "",
      location_address: location.location_address || "",
    }));
  };

  const saveLocation = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const endpoint = locationModal === "add"
        ? `${API}/projects/${projectId}/locations`
        : `${API}/projects/${projectId}/locations/${locationModal.id}`;
      const method = locationModal === "add" ? "post" : "put";
      await axios[method](endpoint, locationForm, ax());
      setLocationModal(null);
      await refresh();
    } catch (error) {
      alert(error.response?.data?.detail || "Could not save project location");
    } finally {
      setSaving(false);
    }
  };

  const removeLocation = async (location) => {
    if (!window.confirm(`Remove ${location.name} from this project?`)) return;
    await axios.delete(`${API}/projects/${projectId}/locations/${location.id}`, ax());
    await refresh();
  };

  if (!project) {
    return <div className="p-8 text-amber-400">Loading project database…</div>;
  }

  const title = isLocationsView
    ? "Project Locations"
    : isTalent
      ? "Project Talent"
      : "Project Crew";
  const subtitle = isLocationsView
    ? "Parking, hospital details and notes stay specific to this production."
    : isTalent
      ? "Cast members, stand-ins and anyone performing on camera for this production."
      : "Crew assigned to this production. Positions and departments only apply here.";

  return (
    <div className="min-w-0 space-y-7 p-3 sm:p-6 md:p-8">
      <div>
        <button
          type="button"
          data-testid="project-database-back-button"
          onClick={() => navigate(`/projects/${projectId}`)}
          className="mb-4 flex items-center gap-2 text-sm text-gray-500 hover:text-amber-400"
        >
          <ArrowLeft size={15} /> Back to {project.name}
        </button>
        <p className="font-mono text-xs uppercase tracking-widest text-amber-400/80">{project.name}</p>
        <h1 className="font-[Outfit] text-4xl font-extrabold text-white sm:text-5xl">{title}</h1>
        <p className="mt-2 text-sm text-gray-500">{subtitle}</p>
      </div>

      {!isLocationsView && !isTalent && (
        <DepartmentManager
          project={project}
          projectId={projectId}
          deptTemplates={deptTemplates}
          setDeptTemplates={setDeptTemplates}
          onUpdate={(nextList) => setProject((p) => (p ? { ...p, departments: nextList } : p))}
        />
      )}

      {!isLocationsView && (
        <section data-testid="project-crew-database" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500">{crew.length} {isTalent ? "talent" : "crew"} on this project</p>
            <div className="flex flex-wrap gap-2">
              {!isTalent && (
                <button
                  type="button"
                  data-testid="project-crew-csv-import-button"
                  onClick={() => setCsvImportOpen(true)}
                  className="flex items-center gap-2 rounded border border-[#28303F] px-4 py-2 text-sm text-gray-300 hover:border-amber-500/40 hover:text-amber-400"
                >
                  <FileUp size={15} /> Import CSV
                </button>
              )}
              <button
                type="button"
                data-testid={isTalent ? "project-add-talent-button" : "project-add-crew-button"}
                onClick={() => openCrew()}
                className="flex items-center gap-2 rounded bg-amber-500 px-4 py-2 text-sm font-bold text-black hover:bg-amber-600"
              >
                <Plus size={15} /> {isTalent ? "Add talent" : "Add crew"}
              </button>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-[#28303F] bg-[#14181F]">
            {crew.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">
                {isTalent
                  ? "No talent added yet. Pick someone from the company people database or enter a new performer."
                  : "No crew yet. Add from the company database, enter a new person, or import a CSV."}
              </p>
            ) : (
              <div className="divide-y divide-[#28303F]">
                {crew.map((member) => (
                  <div key={member.id} data-testid="project-crew-row" className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="font-medium text-white">{member.name}</p>
                      <p className="text-xs text-gray-500">
                        {isTalent
                          ? (member.role || "Character not set")
                          : `${member.role || "Position not set"}${member.department ? ` · ${member.department}` : ""}`}
                      </p>
                      <p className="text-xs text-gray-500">{member.phone || "No phone"} · {member.email || "No email"}</p>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" data-testid="project-crew-edit-button" onClick={() => openCrew(member)} className="p-2 text-gray-500 hover:text-blue-400"><Edit3 size={15} /></button>
                      <button type="button" data-testid="project-crew-remove-button" onClick={() => removeCrew(member)} className="p-2 text-gray-500 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {isLocationsView && (
        <section data-testid="project-locations-database" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500">{locations.length} locations on this project</p>
            <button type="button" data-testid="project-add-location-button" onClick={() => openLocation()} className="flex items-center gap-2 rounded bg-amber-500 px-4 py-2 text-sm font-bold text-black hover:bg-amber-600"><Plus size={15} /> Add location</button>
          </div>
          <div className="overflow-hidden rounded-xl border border-[#28303F] bg-[#14181F]">
            {locations.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">Add from the company database or enter a new location.</p>
            ) : (
              <div className="divide-y divide-[#28303F]">
                {locations.map((location) => (
                  <div key={location.id} data-testid="project-location-row" className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="font-medium text-white">{location.name}</p>
                      <p className="whitespace-pre-line text-xs text-gray-500">{location.location_address || location.location_name || "No address"}</p>
                      {location.notes && <p className="mt-1 text-xs text-amber-300/80">{location.notes}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button type="button" data-testid="project-location-edit-button" onClick={() => openLocation(location)} className="p-2 text-gray-500 hover:text-blue-400"><Edit3 size={15} /></button>
                      <button type="button" data-testid="project-location-remove-button" onClick={() => removeLocation(location)} className="p-2 text-gray-500 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {crewModal && (
        <Modal title={`${crewModal === "add" ? "Add" : "Edit"} ${isTalent ? "Talent" : "Crew"}`} onClose={() => setCrewModal(null)}>
          <form onSubmit={saveCrew} className="space-y-4 p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs text-gray-500">
                Full name
                {crewModal === "add" ? (
                  <AutocompleteNameInput
                    value={crewForm.name}
                    required
                    onChange={(name) => setCrewForm((prev) => ({ ...prev, name, company_crew_id: "" }))}
                    onPick={(member) => chooseCompanyCrew(member.id)}
                    source={companyCrew.filter((member) => !crew.some((row) => row.id === member.id))}
                    testid={isTalent ? "project-talent-name-input" : "project-crew-name-input"}
                    placeholder={isTalent ? "Type a performer's name…" : "Type a crew member's name…"}
                    emptyHint={isTalent ? "Enter a new performer…" : "Enter a new person…"}
                  />
                ) : (
                  <input
                    required
                    value={crewForm.name}
                    onChange={(event) => setCrewForm({ ...crewForm, name: event.target.value })}
                    className={`${inputClass}${inputFocus} mt-1`}
                  />
                )}
              </label>
              <label className="text-xs text-gray-500">Phone<input value={crewForm.phone} onChange={(event) => setCrewForm({ ...crewForm, phone: formatCrewPhone(event.target.value) })} className={`${inputClass}${inputFocus} mt-1`} /></label>
              <label className="text-xs text-gray-500">Email<input type="email" value={crewForm.email} onChange={(event) => setCrewForm({ ...crewForm, email: event.target.value })} className={`${inputClass}${inputFocus} mt-1`} /></label>
              <label className="text-xs text-gray-500">{isTalent ? "Character" : "Position"}<input value={crewForm.role} onChange={(event) => setCrewForm({ ...crewForm, role: event.target.value })} data-testid={isTalent ? "project-talent-character-input" : "project-crew-position-input"} className={`${inputClass}${inputFocus} mt-1`} /></label>
            </div>
            {!isTalent && (
              <DepartmentChipPicker
                departments={project?.departments || []}
                value={crewForm.department}
                onChange={(value) => setCrewForm({ ...crewForm, department: value })}
                onAddDepartment={addProjectDepartment}
                templateSuggestions={
                  Array.from(
                    new Map(
                      (deptTemplates || [])
                        .flatMap((t) => t.departments || [])
                        .map((d) => [(d || "").toLowerCase(), d]),
                    ).values(),
                  ).filter(Boolean)
                }
              />
            )}
            <label className="block text-xs text-gray-500">Notes<textarea value={crewForm.notes} onChange={(event) => setCrewForm({ ...crewForm, notes: event.target.value })} className={`${inputClass}${inputFocus} mt-1 resize-y`} rows={2} /></label>
            <button data-testid={isTalent ? "project-talent-save-button" : "project-crew-save-button"} disabled={saving} className="w-full rounded bg-amber-500 py-2.5 text-sm font-bold text-black disabled:opacity-50">{saving ? "Saving…" : `Save ${isTalent ? "talent" : "crew"}`}</button>
          </form>
        </Modal>
      )}

      {locationModal && (
        <Modal title={locationModal === "add" ? "Add Project Location" : "Edit Project Location"} onClose={() => setLocationModal(null)}>
          <form onSubmit={saveLocation} className="space-y-4 p-5">
            <label className="block text-xs text-gray-500">
              Location name
              {locationModal === "add" ? (
                <AutocompleteNameInput
                  value={locationForm.name}
                  required
                  onChange={(name) => setLocationForm((prev) => ({ ...prev, name, company_location_id: "" }))}
                  onPick={(location) => chooseCompanyLocation(location.id)}
                  source={companyLocations.filter((location) => !locations.some((row) => row.id === location.id))}
                  testid="project-location-name-input"
                  placeholder="Type a location name…"
                  emptyHint="Enter a new location…"
                />
              ) : (
                <input
                  required
                  value={locationForm.name}
                  onChange={(event) => setLocationForm({ ...locationForm, name: event.target.value })}
                  className={`${inputClass}${inputFocus} mt-1`}
                />
              )}
            </label>
            <label className="block text-xs text-gray-500">Shared company address<textarea value={locationForm.location_address} onChange={(event) => setLocationForm({ ...locationForm, location_address: event.target.value })} className={`${inputClass}${inputFocus} mt-1 resize-y`} rows={3} /></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs text-gray-500">Project parking<textarea value={locationForm.parking_address} onChange={(event) => setLocationForm({ ...locationForm, parking_address: event.target.value })} className={`${inputClass}${inputFocus} mt-1 resize-y`} rows={2} /></label>
              <label className="text-xs text-gray-500">Project notes<textarea value={locationForm.notes} onChange={(event) => setLocationForm({ ...locationForm, notes: event.target.value })} className={`${inputClass}${inputFocus} mt-1 resize-y`} rows={2} /></label>
            </div>
            <label className="block text-xs text-gray-500">Nearest hospital<input value={locationForm.hospital_name} onChange={(event) => setLocationForm({ ...locationForm, hospital_name: event.target.value })} className={`${inputClass}${inputFocus} mt-1`} /></label>
            <button data-testid="project-location-save-button" disabled={saving} className="w-full rounded bg-amber-500 py-2.5 text-sm font-bold text-black disabled:opacity-50">{saving ? "Saving…" : "Save project location"}</button>
          </form>
        </Modal>
      )}

      {csvImportOpen && (
        <CrewCsvImportModal
          projectId={projectId}
          project={project}
          onClose={() => setCsvImportOpen(false)}
          onComplete={() => { refresh().catch(() => {}); }}
        />
      )}
    </div>
  );
}